import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import SortIcon from 'assets/icons/iconoir_sort-up.svg?react';

const EXPAND_DURATION = 500;
const FADE_DURATION = 300;
const IDLE_COLLAPSE_MS = 5000;

const SortContainer = styled('div', {
  shouldForwardProp: (p) => p !== 'expanded' && p !== 'isMobile'
})(({ theme, expanded, isMobile }) => ({
  position: 'relative',
  marginRight: 10,
  height: 44,
  flexShrink: 0,
  width: isMobile ? (expanded ? 170 : 42) : 204,
  transition: `width ${EXPAND_DURATION}ms ease`
}));

const CollapsedSortButton = styled('button', {
  shouldForwardProp: (p) => p !== 'visible'
})(({ visible }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 42,
  height: 42,
  borderRadius: 8,
  border: 'none',
  padding: 0,
  margin: 0,
  overflow: 'hidden',
  backgroundColor: '#B9F833',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  transition: `opacity ${FADE_DURATION}ms ease`,
  '& svg': {
    width: 42,
    height: 42,
    display: 'block',
    transform: 'scale(0.85)'
  }
}));

const SortFormControl = styled(FormControl, {
  shouldForwardProp: (p) => p !== 'visible'
})(({ visible }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  opacity: visible ? 1 : 0,
  pointerEvents: visible ? 'auto' : 'none',
  transition: `opacity ${FADE_DURATION}ms ease ${visible ? EXPAND_DURATION - FADE_DURATION : 0}ms`,
  '& .MuiOutlinedInput-root': {
    height: 44
  },
  '& .MuiSelect-select': {
    fontSize: 14,
    paddingTop: 0,
    paddingBottom: 0,
    display: 'flex',
    alignItems: 'center'
  }
}));

const menuProps = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        fontSize: 14,
        minHeight: 36
      }
    }
  }
};

const SORT_OPTIONS = [
  { value: 'popularity-desc', column: 'popularity', direction: 'desc', labelKey: 'ByPopularity' },
  { value: 'name-asc', column: 'name', direction: 'asc', labelKey: 'ByNameAsc' },
  { value: 'name-desc', column: 'name', direction: 'desc', labelKey: 'ByNameDesc' }
];

const DEFAULT_VALUE = 'popularity-desc';

const sortToValue = (sort) => {
  if (!sort || typeof sort !== 'object') return DEFAULT_VALUE;
  const [column, direction] = Object.entries(sort)[0] || [];
  if (!column) return DEFAULT_VALUE;
  const match = SORT_OPTIONS.find(
    (opt) => opt.column === column && opt.direction === direction
  );
  return match ? match.value : DEFAULT_VALUE;
};

const SortComponent = React.forwardRef(({ sort, actions }, ref) => {
  const t = useTranslate('Professions');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const idleTimerRef = React.useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      collapse: () => {
        setMenuOpen(false);
        setExpanded(false);
      }
    }),
    []
  );

  const value = sortToValue(sort);
  const showFull = !isMobile || expanded;

  const clearIdleTimer = React.useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const armIdleTimer = React.useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      setExpanded(false);
      setMenuOpen(false);
    }, IDLE_COLLAPSE_MS);
  }, [clearIdleTimer]);

  // Auto-collapse on mobile after idle. Reset timer on interaction signals.
  React.useEffect(() => {
    if (!isMobile || !expanded) {
      clearIdleTimer();
      return undefined;
    }
    if (menuOpen) {
      // Pause the timer while the user is browsing the menu.
      clearIdleTimer();
      return undefined;
    }
    armIdleTimer();
    return clearIdleTimer;
  }, [isMobile, expanded, menuOpen, armIdleTimer, clearIdleTimer]);

  // Reset expanded state when we leave mobile so desktop is clean.
  React.useEffect(() => {
    if (!isMobile) {
      setExpanded(false);
      clearIdleTimer();
    }
  }, [isMobile, clearIdleTimer]);

  // Cleanup on unmount.
  React.useEffect(() => clearIdleTimer, [clearIdleTimer]);

  const handleExpand = React.useCallback(() => {
    setExpanded(true);
    // Wait for the width animation to finish before opening the menu so it
    // doesn't pop out of a 42px wide anchor.
    setTimeout(() => setMenuOpen(true), EXPAND_DURATION);
  }, []);

  const handleChange = React.useCallback(
    (event) => {
      const selected = SORT_OPTIONS.find((opt) => opt.value === event.target.value);
      if (!selected) return;
      actions.onSortChange(selected.column, selected.direction);
    },
    [actions]
  );

  return (
    <SortContainer expanded={expanded} isMobile={isMobile}>
      {isMobile && (
        <CollapsedSortButton
          type="button"
          aria-label={t('Sort')}
          visible={!expanded}
          onClick={handleExpand}>
          <SortIcon />
        </CollapsedSortButton>
      )}
      <SortFormControl visible={showFull}>
        <Select
          value={value}
          onChange={handleChange}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
          input={<OutlinedInput />}
          inputProps={{ 'aria-label': t('Sort') }}
          MenuProps={menuProps}>
          {SORT_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {t(opt.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </SortFormControl>
    </SortContainer>
  );
});

SortComponent.displayName = 'SortComponent';

export default SortComponent;
